import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './Accordion.module.scss';
import { UI_AccordionItem } from './AccordionItem/AccordionItem';
import type { AccordionItemProps } from './AccordionItem/AccordionItem';
import { UI_Heading } from 'ui/components/1-atoms/Heading/Heading';
import { resolveHashTarget } from './accordionAnchor';

export interface AccordionProps {
	className?: string;
	accordionItems: AccordionItemProps[];
	accordionHeading: string;
	allowMultipleOpen?: boolean;
}

export const UI_Accordion = ({ className, accordionItems, accordionHeading, allowMultipleOpen }: AccordionProps) => {
	const initialMulti = accordionItems.map((item) => !!item.openByDefault);
	const initialSingle = initialMulti.findIndex(Boolean);

	const [openState, setOpenState] = useState<boolean[] | number>(allowMultipleOpen ? initialMulti : initialSingle);
	const handledHash = useRef<string | null>(null);

	const handleToggle = (index: number) => {
		if (allowMultipleOpen) {
			setOpenState((prev) => {
				const arr = [...(prev as boolean[])];
				arr[index] = !arr[index];
				return arr;
			});
		} else {
			setOpenState((prev) => ((prev as number) === index ? -1 : index));
		}
	};

	// Open and scroll to the item the url hash points at, on load as well as when the hash
	// changes on an already open page.
	useEffect(() => {
		const openHashTarget = () => {
			const hash = window.location.hash;

			// Re-renders re-run this effect; without the guard a closed item would spring
			// back open every time the parent renders.
			if (handledHash.current === hash) return;
			handledHash.current = hash;

			const target = resolveHashTarget(accordionItems, hash);

			if (!target) return;

			// A hash written by title, or one carrying a title that has since been renamed,
			// gets swapped for the durable `#{slug}-{id}` form. Next.js keeps its routing
			// data in history.state, so pass the current state along untouched.
			if (!target.isCanonical) {
				window.history.replaceState(window.history.state, '', `#${target.anchorId}`);
				handledHash.current = window.location.hash;
			}

			setOpenState((prev) => {
				if (!allowMultipleOpen) return target.index;

				const arr = [...(prev as boolean[])];
				arr[target.index] = true;

				return arr;
			});

			// Wait for the state above to paint, otherwise we scroll to the collapsed item.
			requestAnimationFrame(() => {
				document.getElementById(target.anchorId)?.scrollIntoView({ behavior: 'smooth' });
			});
		};

		openHashTarget();
		window.addEventListener('hashchange', openHashTarget);

		return () => window.removeEventListener('hashchange', openHashTarget);
	}, [accordionItems, allowMultipleOpen]);

	return (
		<div className={classNames(styles.Accordion, className)}>
			{accordionHeading && (
				<UI_Heading headingLevel="h2" className={styles.Accordion_heading} style="lg">
					{accordionHeading}
				</UI_Heading>
			)}

			{accordionItems.map((accordionItem, index) => (
				<UI_AccordionItem
					{...accordionItem}
					key={accordionItem.id ?? index}
					openByDefault={allowMultipleOpen ? (openState as boolean[])[index] : (openState as number) === index}
					onClick={() => handleToggle(index)}
				/>
			))}
		</div>
	);
};
